import type { DocProfileData, DocProfileInput } from '../docs/docProfile';
import type { StyleBox, StyleCat } from '../editor/textFx';
import type { WordEntry } from '../thai/dictionary';

/** Calls to the Worker API. Every failure becomes an ApiError with a Thai reason that can be shown as-is. */

export interface DesignMeta {
  id: string; name: string; width: number; height: number; bg: string; version: number; createdAt: string; updatedAt: string;
  /** number of pages (designs saved before pages existed: 1) */
  pages?: number;
  /** a video project (src/video/project.ts); length worked out by the server */
  kind?: 'design' | 'video';
  durationMs?: number | null;
}

export class ApiError extends Error {
  constructor(public status: number, message: string, public body: Record<string, unknown> = {}) { super(message); }
}

async function call<T>(path: string, init: RequestInit = {}): Promise<T> {
  let res: Response;
  try {
    res = await fetch(path, { credentials: 'same-origin', ...init });
  } catch {
    throw new ApiError(0, 'ติดต่อเซิร์ฟเวอร์ไม่ได้ (ไม่มีอินเทอร์เน็ตหรือเซิร์ฟเวอร์ไม่ตอบ)');
  }
  // anything but our JSON (e.g. a proxy or Cloudflare error page) is reported with its status
  const type = res.headers.get('content-type') ?? '';
  if (!type.includes('application/json')) {
    throw new ApiError(res.status || 0, `เซิร์ฟเวอร์ตอบกลับในรูปแบบที่ไม่รู้จัก (${res.status}) กรุณาลองใหม่`);
  }
  const body = await res.json().catch(() => ({})) as Record<string, unknown>;
  if (!res.ok || body.ok === false) {
    throw new ApiError(res.status, typeof body.error === 'string' ? body.error : `เซิร์ฟเวอร์ตอบผิดพลาด (${res.status})`, body);
  }
  return body as T;
}

/** 0024: how often the app asks about an AI job, and how long it waits in all before saying so */
export const AI_POLL_MS = 4000, AI_WAIT_MAX_MS = 10 * 60_000;
/** said when the app stops waiting: the job goes on, and is collected for "ล่าสุด" — making it again would pay twice */
export const AI_STILL_MAKING = `AI ยังทำงานนี้ไม่เสร็จภายใน ${AI_WAIT_MAX_MS / 60_000} นาที · งานยังทำต่อบนเซิร์ฟเวอร์ (นับรวมในเพดานแล้ว) `
  + 'ไม่ต้องสร้างใหม่ — เปิดแผงนี้อีกครั้งภายหลัง ผลจะอยู่ในรายการ "ล่าสุด"';
const sleep = (ms: number) => new Promise(r => setTimeout(r, ms));
/**
 * Start an AI job (Gemini background mode, 0024) and wait for it: the start answers 202 {job}, then the job is asked
 * about every AI_POLL_MS. The result, or the server's reason, comes back as from call(). Too long = a Thai reason
 * (the job keeps going on the server and is counted).
 */
async function aiJob<T>(path: string, init: RequestInit): Promise<T> {
  let r = await call<T & { job?: { type: 'image' | 'layout' | 'flow'; id: string; status: string } }>(path, init);
  const job = r.job;
  if (!job) return r;   // an answer that was ready at once
  const until = Date.now() + AI_WAIT_MAX_MS;
  for (;;) {
    await sleep(AI_POLL_MS);
    // the deadline is checked after asking: an iPad that slept past it still gets one last answer
    const late = Date.now() > until;
    try { r = await call(`/api/ai-image/jobs/${job.type}/${job.id}`); }
    catch (e) {
      // no internet for a moment (an iPad waking up), or a passing server error (a Cloudflare 502 page, a D1 hiccup):
      // ask again. Only the server's own verdict on the job (code ai_failed) or a 4xx ends the wait.
      const passing = e instanceof ApiError && (e.status === 0 || (e.status >= 500 && e.body.code !== 'ai_failed'));
      if (!passing) throw e;
      if (!late) continue;
    }
    if (!r.job) return r;
    if (late) throw new ApiError(0, AI_STILL_MAKING);
  }
}

const jsonInit = (method: string, data: unknown): RequestInit =>
  ({ method, headers: { 'content-type': 'application/json' }, body: JSON.stringify(data) });

/** ข้อมูลบริษัท/แบรนด์ ของเอกสาร (0029, src/docs/docProfile.ts); `data` null = the stored row is broken (said) */
export interface DocProfile { id: string; label: string; data: DocProfileData | null; logo: string | null; updatedAt: string; updatedByName: string }

export interface DesignPayload { name: string; width: number; height: number; bg: string; json: string }

export type Role = 'admin' | 'member';
export type Status = 'pending' | 'active' | 'disabled';

export interface Account {
  id: string; email: string; name: string; role: Role; status: Status;
  createdAt: string; approvedAt: string | null; statusAt: string | null; lastLoginAt: string | null;
  /** 0011: extra rights given to this member (admins can already do everything); missing on an older remembered account */
  perms?: Perm[];
}
/** 0011: extra rights an admin gives a member one by one */
export type Perm = 'templates' | 'ai_music' | 'music_upload' | 'ai_voice' | 'graphics' | 'text_styles' | 'ai_image';
export const PERM_LIST: Array<{ perm: Perm; name: string; hint: string }> = [
  { perm: 'templates', name: 'บันทึกงานเป็นเทมเพลต', hint: 'งานของตัวเอง (ซ่อน ลบ จัดหมวด ยังเป็นของผู้ดูแล)' },
  { perm: 'ai_music', name: 'ให้ AI แต่งเพลง', hint: 'เห็นเฉพาะเพลงร่างของตัวเอง ใช้เพดานเงินรวมของกลุ่ม' },
  { perm: 'music_upload', name: 'อัปโหลดเพลงเข้าคลัง', hint: 'ต้องบอกที่มาและยืนยันสิทธิ์ใช้เพลง' },
  { perm: 'ai_voice', name: 'เสียงพากย์ AI', hint: 'ให้ AI พูดข้อความของแต่ละฉากในวิดีโอ ใช้เพดานเงินรวมของกลุ่ม' },
  { perm: 'graphics', name: 'เพิ่มกราฟิกเข้าคลัง', hint: 'คลังกราฟิกของกลุ่มในแท็บองค์ประกอบ ต้องยืนยันว่ากลุ่มมีสิทธิ์ใช้ แก้/ซ่อน/ลบได้เฉพาะชิ้นที่ตัวเองเพิ่ม' },
  { perm: 'text_styles', name: 'บันทึกแบบข้อความของกลุ่ม', hint: 'บันทึกข้อความที่จัดไว้เป็นแบบให้ทุกคนใช้ ลบได้เฉพาะของตัวเอง' },
  { perm: 'ai_image', name: 'รูป AI', hint: 'สร้างรูปด้วย AI ใส่ช่องใส่รูปในงานของตัวเอง ใช้เพดานเงินรวมของกลุ่ม' }
];

/** เสียงพากย์ AI (0016) */
export interface VoiceInfo {
  month: string; capUsd: number; usedUsd: number; canUse: boolean;
  services: Record<'gemini' | 'eleven', { model: string; problem: string | null }>;
  geminiVoices: Array<{ id: string; th: string }>;
  prices: { geminiUsdPerMinute: number; elevenUsdPer1000Chars: number };
}
export interface MadeVoice { id: string; contentType: string; seconds: number | null; costUsd: number }
/** รูปประกอบด้วย AI (0022) */
export type AiImageSize = '1K' | '2K';
export interface AiImageInfo {
  month: string; capUsd: number; usedUsd: number; canUse: boolean; model: string;
  /** why it cannot be used yet (the Gemini key), or null */
  problem: string | null;
  prices: Record<AiImageSize, number>; estimates: Record<AiImageSize, number>;
  /** a slot whose longer side is more than this (px on its page) gets a 2K picture */
  twoKFromPx: number;
  /** AI สร้างเทมเพลต (0023): the most one layout can cost today; missing from an older server */
  /** เขียน prompt สำหรับ Google Flow (0025): the most one set can cost today; missing from an older server */
  flow?: { model: string; maxUsd: number };
  layout?: { model: string; maxUsd: number; /** รูปตัวอย่าง: the most bytes the server takes (missing = an older server, no รูปตัวอย่าง) */ refMaxBytes?: number };
}
/** AI สร้างเทมเพลต (0023): a layout checked by the server (src/templates/aiLayout.ts) */
export interface MadeLayout {
  id: string; name: string; notes: string[]; w: number; h: number; costUsd: number;
  /** Gemini's answer as it came: the app checks it with the same checker as the server (BUG-13, BUG-72) */
  raw: unknown;
}
export interface MadeImage { id: string; url: string; width: number; height: number; costUsd: number; size: AiImageSize; aspect: string }
/** 0024: my AI jobs of the last 24 h (GET /api/ai-image/recent) — 'making' = still at Gemini, 'failed' has its reason */
export type AiStatus = 'making' | 'done' | 'failed';
export interface RecentImage {
  id: string; status: AiStatus; kind: 'slot' | 'template'; prompt: string; size: AiImageSize; aspect: string; costUsd: number;
  error: string | null; createdAt: string; url: string | null; width: number | null; height: number | null;
}
/** เขียน prompt สำหรับ Google Flow (0025): Gemini's answer as it came (checked again in the app, BUG-13/72) + the request */
export interface MadeFlow { id: string; raw: unknown; request: import('../video/flowPrompt').FlowRequest; costUsd: number; /** 0026: something about the set the person must know */ warning?: string }
export interface RecentFlow { id: string; status: AiStatus; brief: string; title: string | null; lang: 'en' | 'th'; aspect: string; total: number; costUsd: number; error: string | null; createdAt: string }
export interface RecentLayout { id: string; status: AiStatus; brief: string; name: string | null; w: number; h: number; costUsd: number; error: string | null; createdAt: string }
/** a ready-made template's AI pictures: its slots that have one (`url` for admins) and its preview when up to date */
export interface TplAiState { slots: Array<{ index: number; imageId: string; url?: string }>; thumb: string | null; madeFrom: string }
/** may this account do it? admins always; members only with the right (the server checks every request too) */
export const canDo = (a: Account | null | undefined, p: Perm): boolean =>
  !!a && a.status === 'active' && (a.role === 'admin' || (a.perms ?? []).includes(p));
export interface Me {
  user: Account; session: { createdAt: string; expiresAt: string };
  server: boolean; version: string; pending: number;
}
export interface AdminUser extends Account {
  approvedBy: string | null; statusBy: string | null; designs: number; fixedAdmin: boolean;
  /** 0011: who gave each right, and when */
  permInfo?: Array<{ perm: Perm; at: string; by: string }>;
}
export type AdminAction = 'approve' | 'disable' | 'enable' | 'make_admin' | 'make_member';
export interface Person { id: string; name: string; email: string }
export interface OwnerInfo { name: string; email: string; status: Status }
/** A design in "งานของฉัน", with how widely it is shared; `editedBy`/`madeBy` when an admin saved it last / made it (0015) */
export interface MyDesign extends DesignMeta {
  shares: number; groupShared: boolean;
  editedBy?: { name: string; at: string } | null; madeBy?: { name: string; at: string } | null;
}
/** "งานของทุกคน" (admins, 0015): any member's work with its owner */
export interface AnyDesign extends DesignMeta {
  ownerId: string; mine: boolean; owner: OwnerInfo; editedBy: { name: string; at: string } | null;
}
/** what admins did to a work (0015): a run of autosaves is one 'edit' (firstAt … at) */
export interface AdminEdit { action: 'create' | 'edit' | 'delete'; version: number | null; firstAt: string; at: string; by: string }
/** where a work made for a member starts (3ก) */
export type MakeFor =
  | { from: 'blank'; design: DesignPayload }
  | { from: 'template'; templateId: string }
  | { from: 'copy'; designId: string };
/** A design someone else shared with me. */
export interface SharedDesign extends DesignMeta { sharedAt: string; via: 'direct' | 'group'; owner: OwnerInfo }
export interface ShareInfo {
  group: { sharedAt: string } | null;
  people: Array<Person & { status: Status | null; sharedAt: string }>;
}
export interface TemplateCategory { id: string; name: string; count: number; sort?: number }
export interface TemplateInfo {
  id: string; name: string; categoryId: string; width: number; height: number; bg: string; version: number;
  hidden: boolean; uses: number; updatedAt: string; thumb: string; updatedByName?: string;
  pages?: number;
  /** 0008: a video template (slots to fill), its length and an optional preview MP4 address */
  kind?: 'design' | 'video'; durationMs?: number | null; slots?: number | null; preview?: string | null;
}
/** a song of the group's music library (0009); `works`/`templates`: admins only, how many use it */
export interface MusicInfo {
  id: string; name: string; source: string; categoryId: string; size: number; durationMs: number; hidden: boolean;
  createdAt: string; createdByName: string; file: string; works?: number; templates?: number;
}
export interface MusicCategory { id: string; name: string; sort: number }
/** คลังกราฟิก (0019): a graphic of the group's library; `mine` = this account added it */
export interface GraphicInfo {
  id: string; name: string; keywords: string; categoryId: string; type: string; size: number; hidden: boolean;
  createdAt: string; createdByName: string; mine: boolean; file: string;
}
export interface GraphicCategory { id: string; name: string; sort: number }
/** แบบข้อความของกลุ่ม (0021) */
export interface SavedTextStyle { id: string; name: string; cat: StyleCat; boxes: StyleBox[]; createdAt: string; createdByName: string; mine: boolean }
/** a free set (Fluent Emoji / Phosphor Icons) and how far an admin has copied it into the group's storage */
export interface FreeSetInfo {
  set: 'fluent' | 'phosphor'; catalog: string; name: string; license: string; url: string;
  files: number; chunks: number; done: number; ready: boolean;
  /** admins (หลังบ้าน): the parts not copied yet */
  left?: number[];
}
/** AI music (0010): one request to ElevenLabs — a draft to listen to, or why it failed */
export interface AiMusicJob {
  id: string; status: 'making' | 'draft' | 'kept' | 'discarded' | 'failed'; mood: string; moodName: string; extra: string;
  lengthMs: number; charged: boolean; error: string | null; createdAt: string; byName: string;
  /** still "making" long after it should have finished: the answer was lost, it can be removed */
  stale: boolean;
  file: string | null;
}
export interface AiMusicInfo {
  keySet: boolean; /** the key set cannot be an API key (e.g. its ID): why, in Thai */ keyProblem: string | null; month: string; capMinutes: number; usedMs: number; usdPerMinute: number;
  moods: Array<{ id: string; name: string }>; jobs: AiMusicJob[];
}
export interface PrintProfileInfo {
  id: string; name: string; url: string; condition: string; registry: string; note: string; builtIn: boolean;
  bytes?: number; uploadedAt?: string; uploadedBy?: string;
}
/* ลบพื้นหลัง on the group's VM (0012) */
export interface BgResult { url: string; originalUrl: string; ms: number; model: string; width: number | null; height: number | null; used: number; limit: number }
export interface BgAdmin {
  problem: string | null; healthError: string | null; dailyLimit: number; model: string; limitMax: number; doneToday: number; day: string;
  /** ลบด้วย AI (0018): its own limit per person per day and today's count; absent from a Worker before it */
  eraseLimit?: number; eraseDoneToday?: number;
  health: { version: string; models: Array<{ id: string; name: string; loaded: boolean }>; busy: boolean; waiting: number; queue: number; peakRamGb: number; cpus: number; erase?: { installed: boolean; loaded: boolean } } | null;
  models: Array<{ id: string; name: string }>;
  jobs: Array<{ id: string; by: string; model: string; kind?: 'remove' | 'erase'; ms: number | null; at: string; error: string | null; status: 'working' | 'done' | 'failed' | 'lost' }>;
}
/** ลบด้วย AI: the filled-in patch and today's count */
export interface EraseResult { blob: Blob; ms: number; used: number; limit: number }

/** ลบด้วย AI (0018): a patch of a picture + its mask → the patch filled in (a PNG, not JSON — errors are JSON) */
async function eraseCall(image: Blob, mask: Blob): Promise<EraseResult> {
  const form = new FormData();
  form.append('image', image, 'patch');
  form.append('mask', mask, 'mask.png');
  let res: Response;
  try { res = await fetch('/api/bg/erase', { method: 'POST', credentials: 'same-origin', body: form }); }
  catch { throw new ApiError(0, 'ติดต่อเซิร์ฟเวอร์ไม่ได้ (ไม่มีอินเทอร์เน็ตหรือเซิร์ฟเวอร์ไม่ตอบ)'); }
  const type = res.headers.get('content-type') ?? '';
  if (res.ok && type.startsWith('image/png')) {
    const blob = await res.blob().catch(() => null);
    if (!blob || !blob.size) throw new ApiError(res.status, 'รับผลลบด้วย AI ไม่ครบ (การเชื่อมต่อขาด) ลองใหม่อีกครั้ง');
    return { blob, ms: Number(res.headers.get('x-bg-ms')) || 0, used: Number(res.headers.get('x-erase-used')) || 0, limit: Number(res.headers.get('x-erase-limit')) || 0 };
  }
  const body = type.includes('application/json') ? await res.json().catch(() => ({})) as Record<string, unknown> : {};
  throw new ApiError(res.status, typeof body.error === 'string' ? body.error : `เซิร์ฟเวอร์ตอบผิดพลาด (${res.status})`, body);
}

export interface SettingsInfo { sessionDays: number; updatedAt: string | null; updatedBy: string | null; stored: boolean }

export const api = {
  authStatus: () => call<{ ready: boolean; reason: string | null; version: string }>('/api/auth/status'),
  me: () => call<Me>('/api/me'),
  logout: () => call<{ ok: true }>('/api/auth/logout', { method: 'POST' }),
  list: () => call<{ designs: MyDesign[] }>('/api/designs'),
  /** access 'admin' (0015): an admin opened a member's work — read-only until "แก้ไขในฐานะผู้ดูแล" */
  get: (id: string) => call<{ design: DesignMeta; json: string; access: 'owner' | 'view' | 'admin'; owner: OwnerInfo | null }>(`/api/designs/${id}`),
  history: (id: string) => call<{ history: AdminEdit[] }>(`/api/designs/${id}/history`),
  create: (d: DesignPayload) => call<{ design: DesignMeta }>('/api/designs', jsonInit('POST', d)),
  save: (id: string, d: DesignPayload, baseVersion: number) =>
    call<{ design: DesignMeta }>(`/api/designs/${id}`, jsonInit('PUT', { ...d, baseVersion })),
  remove: (id: string) => call<{ ok: true }>(`/api/designs/${id}`, { method: 'DELETE' }),
  upload: (blob: Blob) => call<{ url: string }>('/api/assets', { method: 'POST', headers: { 'content-type': blob.type }, body: blob }),
  /* ข้อมูลบริษัท/แบรนด์ ของเอกสาร (0029) */
  docProfiles: () => call<{ profiles: DocProfile[]; canManage: boolean }>('/api/doc-profiles'),
  addDocProfile: (p: DocProfileInput) => call<{ profile: DocProfile }>('/api/doc-profiles', jsonInit('POST', p)),
  saveDocProfile: (id: string, p: DocProfileInput, updatedAt: string) => call<{ profile: DocProfile }>(`/api/doc-profiles/${id}`, jsonInit('PUT', { ...p, updatedAt })),
  deleteDocProfile: (id: string, updatedAt: string) => call<{ ok: true }>(`/api/doc-profiles/${id}`, jsonInit('DELETE', { updatedAt })),
  takeDocLogo: (id: string) => call<{ url: string }>(`/api/doc-profiles/${id}/logo`, { method: 'POST' }),
  bgRemove: (blob: Blob) => call<BgResult>('/api/bg/remove', { method: 'POST', headers: { 'content-type': blob.type }, body: blob }),
  bgErase: eraseCall,

  /* sharing */
  shared: () => call<{ designs: SharedDesign[] }>('/api/designs/shared'),
  copy: (id: string) => call<{ design: DesignMeta }>(`/api/designs/${id}/copy`, { method: 'POST' }),
  shares: (id: string) => call<ShareInfo>(`/api/designs/${id}/shares`),
  share: (id: string, to: 'group' | string) => call<{ already: boolean }>(`/api/designs/${id}/shares`, jsonInit('POST', { to })),
  unshare: (id: string, to: 'group' | string) => call<{ ok: true }>(`/api/designs/${id}/shares/${encodeURIComponent(to)}`, { method: 'DELETE' }),
  people: () => call<{ people: Person[] }>('/api/people'),

  /* templates */
  templates: () => call<{ categories: TemplateCategory[]; templates: TemplateInfo[] }>('/api/templates'),
  useTemplate: (id: string) => call<{ design: DesignMeta }>(`/api/templates/${id}/use`, { method: 'POST' }),
  /** a group template's pages to put into the open work (26 ต.ค. 2569); its pictures become this account's too */
  takeTemplate: (id: string) => call<{ json: string; width: number; height: number; name: string }>(`/api/templates/${id}/take`, { method: 'POST' }),
  designTemplate: (designId: string) => call<{ template: TemplateInfo | null }>(`/api/designs/${designId}/template`),
  /** `templateId`: the template the panel showed (null = none); the server refuses when it is not the one there (deleted meanwhile…) */
  publishTemplate: (designId: string, t: { name: string; categoryId: string; thumb: string; baseVersion: number; templateId: string | null }) =>
    call<{ created: boolean; template: TemplateInfo }>(`/api/designs/${designId}/template`, jsonInit('PUT', t)),
  /* music library (0009) */
  music: () => call<{ categories: MusicCategory[]; music: MusicInfo[] }>('/api/music'),
  /** the song file; a Thai reason when it is gone (deleted from the library) or cannot be fetched */
  musicFile: async (id: string): Promise<Blob> => {
    let res: Response;
    try { res = await fetch(`/api/music/${id}/file`, { credentials: 'same-origin' }); }
    catch { throw new ApiError(0, 'ติดต่อเซิร์ฟเวอร์ไม่ได้ (ไม่มีอินเทอร์เน็ต) จึงดาวน์โหลดเพลงไม่ได้'); }
    if (!res.ok) {
      const body = await res.json().catch(() => ({})) as { error?: string };
      throw new ApiError(res.status, body.error ?? `ดาวน์โหลดเพลงไม่สำเร็จ (${res.status})`);
    }
    return res.blob();
  },
  uploadMusic: (file: Blob, meta: { name: string; source: string; categoryId: string; durationMs: number }) => {
    const q = new URLSearchParams({ name: meta.name, source: meta.source, category: meta.categoryId, durationMs: String(meta.durationMs), rights: '1' });
    // some browsers give no type (or an unusual one) for m4a/wav: the file's extension decides then
    const ext = (file instanceof File ? file.name : '').toLowerCase().split('.').pop() ?? '';
    const byExt: Record<string, string> = { mp3: 'audio/mpeg', m4a: 'audio/mp4', aac: 'audio/aac', wav: 'audio/wav' };
    const type = file.type && file.type !== 'application/octet-stream' ? file.type : byExt[ext] ?? '';
    return call<{ music: MusicInfo }>(`/api/admin/music?${q}`, { method: 'POST', headers: { 'content-type': type || 'application/octet-stream' }, body: file });
  },
  patchMusic: (id: string, p: { name?: string; source?: string; categoryId?: string; hidden?: boolean }) =>
    call<{ ok: true }>(`/api/admin/music/${id}`, jsonInit('PATCH', p)),
  deleteMusic: (id: string, force: boolean) => call<{ ok: true }>(`/api/admin/music/${id}${force ? '?force=1' : ''}`, { method: 'DELETE' }),
  addMusicCategory: (name: string) => call<{ ok: true }>('/api/admin/music-categories', jsonInit('POST', { name })),
  /* แบบข้อความของกลุ่ม (0021) */
  textStyles: () => call<{ styles: SavedTextStyle[] }>('/api/text-styles'),
  saveTextStyle: (s: { name: string; cat: StyleCat; boxes: StyleBox[] }) => call<{ style: SavedTextStyle }>('/api/text-styles', jsonInit('POST', s)),
  deleteTextStyle: (id: string) => call<{ ok: true }>(`/api/text-styles/${id}`, { method: 'DELETE' }),
  /* คลังกราฟิก (0019) */
  graphics: () => call<{ categories: GraphicCategory[]; graphics: GraphicInfo[]; free: FreeSetInfo[] }>('/api/graphics'),
  addGraphic: (file: Blob, meta: { name: string; keywords: string; categoryId: string }) => {
    const q = new URLSearchParams({ name: meta.name, keywords: meta.keywords, category: meta.categoryId, rights: '1' });
    // some browsers give an SVG no type: the file's extension decides then
    const ext = (file instanceof File ? file.name : '').toLowerCase().split('.').pop() ?? '';
    const byExt: Record<string, string> = { svg: 'image/svg+xml', png: 'image/png', jpg: 'image/jpeg', jpeg: 'image/jpeg', webp: 'image/webp' };
    const type = file.type && file.type !== 'application/octet-stream' ? file.type : byExt[ext] ?? '';
    return call<{ graphic: GraphicInfo }>(`/api/admin/graphics?${q}`, { method: 'POST', headers: { 'content-type': type || 'application/octet-stream' }, body: file });
  },
  patchGraphic: (id: string, p: { name?: string; keywords?: string; categoryId?: string; hidden?: boolean }) =>
    call<{ ok: true }>(`/api/admin/graphics/${id}`, jsonInit('PATCH', p)),
  deleteGraphic: (id: string) => call<{ ok: true }>(`/api/admin/graphics/${id}`, { method: 'DELETE' }),
  addGraphicCategory: (name: string) => call<{ ok: true }>('/api/admin/graphic-categories', jsonInit('POST', { name })),
  deleteGraphicCategory: (id: string) => call<{ ok: true }>(`/api/admin/graphic-categories/${id}`, { method: 'DELETE' }),
  freeStatus: () => call<{ chunkSize: number; sets: FreeSetInfo[] }>('/api/admin/graphics/free'),
  freeImport: (set: string, part: number) => call<{ files: number; already?: boolean }>(`/api/admin/graphics/free/${set}/${part}`, { method: 'POST' }),
  freeVerify: (set: string) => call<{ expected: number; found: number; missing: string[] }>(`/api/admin/graphics/free/${set}/verify`, { method: 'POST' }),
  freeReset: (set: string) => call<{ forgot: number }>(`/api/admin/graphics/free/${set}`, { method: 'DELETE' }),
  /* AI music (0010, admins) */
  aiMusic: () => call<AiMusicInfo>('/api/admin/music/ai'),
  /** เสียงพากย์ AI (0016) */
  voice: {
    info: () => call<VoiceInfo>('/api/voice'),
    elevenVoices: () => call<{ voices: Array<{ id: string; name: string; about: string }> }>('/api/voice/eleven-voices'),
    make: (r: { provider: 'gemini' | 'eleven'; voice: string; style: string; text: string }) =>
      call<{ voice: MadeVoice; usedUsd: number; capUsd: number }>('/api/voice', jsonInit('POST', r)),
    setCap: (capUsd: number) => call<{ capUsd: number }>('/api/voice/settings', jsonInit('PUT', { capUsd })),
    fileUrl: (id: string) => `/api/voice/${id}/file`
  },
  /** รูปประกอบด้วย AI (0022) — a job runs in Gemini's background mode and is waited for here (0024) */
  aiImage: {
    info: () => call<AiImageInfo>('/api/ai-image'),
    make: (r: { prompt: string; noText: boolean; w: number; h: number }) =>
      aiJob<{ image: MadeImage; usedUsd: number; capUsd: number }>('/api/ai-image', jsonInit('POST', r)),
    setCap: (capUsd: number) => call<{ capUsd: number }>('/api/ai-image/settings', jsonInit('PUT', { capUsd })),
    templates: () => call<{ templates: Record<string, TplAiState> }>('/api/ai-image/templates'),
    take: (tpl: string) => call<{ slots: Array<{ index: number; url: string }>; missing: number }>(`/api/ai-image/templates/${encodeURIComponent(tpl)}/take`, { method: 'POST' }),
    makeForTemplate: (tpl: string, slot: number) =>
      aiJob<{ image: MadeImage; usedUsd: number; capUsd: number }>(`/api/ai-image/templates/${encodeURIComponent(tpl)}/${slot}`, jsonInit('POST', {})),
    removeFromTemplate: (tpl: string, slot: number) => call<{ template: TplAiState }>(`/api/ai-image/templates/${encodeURIComponent(tpl)}/${slot}`, { method: 'DELETE' }),
    /** 0024: my AI pictures and layouts of the last 24 h; ones nobody collected are collected first */
    recent: () => call<{ images: RecentImage[]; layouts: RecentLayout[]; flows?: RecentFlow[]; usedUsd: number; capUsd: number }>('/api/ai-image/recent'),
    /** เขียน prompt สำหรับ Google Flow (0025) */
    flow: (r: import('../video/flowPrompt').FlowRequest) => aiJob<{ flow: MadeFlow; usedUsd: number; capUsd: number }>('/api/ai-image/flow', jsonInit('POST', r)),
    flowJob: (id: string) => call<{ flow: MadeFlow; usedUsd: number; capUsd: number }>(`/api/ai-image/jobs/flow/${encodeURIComponent(id)}`),
    /** a finished layout again (from "เทมเพลต AI ล่าสุด") */
    layoutJob: (id: string) => call<{ layout: MadeLayout; usedUsd: number; capUsd: number }>(`/api/ai-image/jobs/layout/${encodeURIComponent(id)}`),
    /** admins: ทดสอบ Gemini กับรูป — one tiny picture sent 4 ways, Google's answer for each (worker/rules.ts PROBE_WAYS) */
    probe: () => call<{ at: string; results: Array<{ id: string; th: string; ok: boolean; say: string; model: string | null; inputTokens: number | null; outputTokens: number | null; seconds?: number; firstSeconds?: number }> }>('/api/ai-image/probe', { method: 'POST' }),
    /** ref = รูปตัวอย่าง (base64 JPG/PNG/WebP), sent on to Gemini and never stored */
    layout: (r: { brief: string; w: number; h: number; ref?: { data: string; type: string } }) =>
      aiJob<{ layout: MadeLayout; usedUsd: number; capUsd: number }>('/api/ai-image/layout', jsonInit('POST', r)),
    putThumb: (tpl: string, from: string, jpeg: Blob) => call<{ thumb: string }>(`/api/ai-image/templates/${encodeURIComponent(tpl)}/thumb?from=${encodeURIComponent(from)}`,
      { method: 'PUT', headers: { 'content-type': 'image/jpeg' }, body: jpeg })
  },
  aiMusicMake: (r: { mood: string; extra: string; lengthMs: number }) =>
    call<{ job: AiMusicJob; usedMs: number; capMinutes: number }>('/api/admin/music/ai', jsonInit('POST', r)),
  aiMusicKeep: (id: string, k: { name: string; categoryId: string; durationMs: number }) =>
    call<{ music: MusicInfo }>(`/api/admin/music/ai/${id}/keep`, jsonInit('POST', k)),
  aiMusicDrop: (id: string) => call<{ ok: true }>(`/api/admin/music/ai/${id}`, { method: 'DELETE' }),
  aiMusicCap: (capMinutes: number) => call<{ capMinutes: number }>('/api/admin/music/ai/settings', jsonInit('PUT', { capMinutes })),
  uploadTemplatePreview: (designId: string, mp4: Blob) =>
    call<{ template: TemplateInfo }>(`/api/designs/${designId}/template/preview`, { method: 'PUT', headers: { 'content-type': 'video/mp4' }, body: mp4 }),

  /* group no-break words */
  words: () => call<{ words: WordEntry[]; canRemoveAll: boolean }>('/api/words'),
  addWord: (word: string) => call<{ already: boolean; message: string; word: WordEntry }>('/api/words', jsonInit('POST', { word })),
  importWords: (words: string[]) => call<{ added: string[]; already: string[]; refused: Array<{ word: string; reason: string }> }>('/api/words/import', jsonInit('POST', { words })),
  removeWord: (word: string) => call<{ message: string }>(`/api/words/${encodeURIComponent(word)}`, { method: 'DELETE' }),

  /** colour profile for print-shop PDFs */
  printProfile: () => call<{ profile: PrintProfileInfo; missing: boolean }>('/api/print-profile'),

  admin: {
    users: () => call<{ users: AdminUser[] }>('/api/admin/users'),
    /** 0015: every member's work (search by work or member name/email, or one member) / make a work for a member */
    designs: (q = '', owner = '') => call<{ designs: AnyDesign[] }>(`/api/admin/designs?${new URLSearchParams({ q, owner })}`),
    makeFor: (ownerId: string, start: MakeFor, name = '') =>
      call<{ design: DesignMeta; owner: OwnerInfo }>('/api/admin/designs', jsonInit('POST', { ownerId, name, ...start })),
    act: (id: string, action: AdminAction) => call<{ user: Account }>(`/api/admin/users/${id}/${action}`, { method: 'POST' }),
    setPerm: (id: string, perm: Perm, on: boolean) => call<{ perms: Perm[] }>(`/api/admin/users/${id}/perms`, jsonInit('POST', { perm, on })),
    settings: () => call<SettingsInfo>('/api/admin/settings'),
    saveSettings: (sessionDays: number) => call<SettingsInfo>('/api/admin/settings', jsonInit('PUT', { sessionDays })),
    bg: () => call<BgAdmin>('/api/admin/bg'),
    saveBg: (dailyLimit: number, model: string, eraseLimit?: number) =>
      call<{ dailyLimit: number; model: string; eraseLimit?: number }>('/api/admin/bg/settings', jsonInit('PUT', { dailyLimit, model, ...(eraseLimit === undefined ? {} : { eraseLimit }) })),
    benchBg: (model: string) => call<{ model: string; ms: number; includesLoading: boolean; peakRamGb: number }>('/api/admin/bg/bench', jsonInit('POST', { model })),
    revokeAll: () => call<{ removed: number }>('/api/admin/sessions/revoke-all', { method: 'POST' }),
    templates: () => call<{ categories: TemplateCategory[]; templates: TemplateInfo[] }>('/api/admin/templates'),
    patchTemplate: (id: string, p: { name?: string; categoryId?: string; hidden?: boolean }) =>
      call<{ ok: true }>(`/api/admin/templates/${id}`, jsonInit('PATCH', p)),
    deleteTemplate: (id: string) => call<{ ok: true }>(`/api/admin/templates/${id}`, { method: 'DELETE' }),
    addCategory: (name: string) => call<{ ok: true }>('/api/admin/template-categories', jsonInit('POST', { name })),
    renameCategory: (id: string, name: string) => call<{ ok: true }>(`/api/admin/template-categories/${id}`, jsonInit('PATCH', { name })),
    deleteCategory: (id: string) => call<{ ok: true }>(`/api/admin/template-categories/${id}`, { method: 'DELETE' }),
    printProfiles: () => call<{ activeId: string; missing: boolean; changedAt: string | null; changedBy: string | null; profiles: PrintProfileInfo[] }>('/api/admin/print-profiles'),
    uploadProfile: (file: File) => call<{ profile: PrintProfileInfo }>('/api/admin/print-profiles', {
      method: 'POST', headers: { 'content-type': 'application/vnd.iccprofile', 'x-file-name': encodeURIComponent(file.name) }, body: file
    }),
    useProfile: (id: string) => call<{ activeId: string; changedAt: string; changedBy: string }>('/api/admin/print-profiles/active', jsonInit('PUT', { id })),
    deleteProfile: (id: string) => call<{ ok: true }>(`/api/admin/print-profiles/${id}`, { method: 'DELETE' })
  }
};

/** This page's version (set at build time); compared with the server's on screen. */
export const WEB_VERSION: string = __APP_VERSION__;
